import Posts, { GoLiveStatus, IPosts } from '@/models/Posts'
import { GAEventTypes, trackCustomGAEvents } from '@ghl/clientportal-core'
import { GroupState, waitForGroupData } from './groups'

import { handlePostEdited } from '@/helper/posts.helper'
import Channel from '@/models/Channels'
import { PostsService } from '@/service/PostsService'
import { useGlobalStore } from '@/store/app'
import { convertToRelativeTime } from '@/util/dateTimeConversion'
import { reactive } from 'vue'
import { ChannelState } from './channels'

class BulkReadManager {
  private bulkReadQueue: { postId: string; channelId: string }[] = []
  private bulkReadPromiseResolvers: ((value: void) => void)[] = []
  private alreadyReadPostIds: Set<string> = new Set()
  private bulkReadTimer: ReturnType<typeof setTimeout> | null = null

  private startBulkReadTimer() {
    if (this.bulkReadTimer) return
    this.bulkReadTimer = setTimeout(() => {
      this.flushBulkReadQueue()
    }, 5000)
  }

  private decrementChannelUnreadCount(channelId: string, decrement: number) {
    const channel = GroupState.state.channelList?.find(
      ch => ch._id === channelId
    )
    if (channel) {
      channel.unreadCount = Math.max(0, (channel.unreadCount || 0) - decrement)
    }
    // Also update in channelMetadata if present
    if (ChannelState.state.channelMetadata?.unreadCounts) {
      ChannelState.state.channelMetadata.unreadCounts[channelId] = Math.max(
        0,
        (ChannelState.state.channelMetadata.unreadCounts[channelId] || 0) -
          decrement
      )
    }
    // Also update in currentChannelData if it's the same channel
    if (ChannelState.state.currentChannelData?._id === channelId) {
      ChannelState.state.currentChannelData.unreadCount = Math.max(
        0,
        (ChannelState.state.currentChannelData.unreadCount || 0) - decrement
      )
    }
  }

  private async flushBulkReadQueue() {
    if (this.bulkReadQueue.length === 0) {
      this.bulkReadTimer = null
      return
    }
    const postsToRead = [...this.bulkReadQueue]
    this.bulkReadQueue.length = 0
    this.bulkReadTimer = null
    try {
      const responses = await PostsService.markPostsAsReadBulk(postsToRead)
      // Map to track how many to decrement per channel
      const channelDecrementMap = new Map<string, number>()
      responses.forEach(res => {
        if (!res.wasAlreadyRead) {
          channelDecrementMap.set(
            res.channelId,
            (channelDecrementMap.get(res.channelId) || 0) + 1
          )
        }
      })
      // Decrement unread count for each channel
      channelDecrementMap.forEach((decrement, channelId) => {
        this.decrementChannelUnreadCount(channelId, decrement)
      })
      // Mark all posts in this batch as read
      postsToRead.forEach(item => this.alreadyReadPostIds.add(item.postId))
    } catch (error) {
      console.error('Error in bulk markPostAsRead:', error)
    } finally {
      // Resolve all promises
      this.bulkReadPromiseResolvers.forEach(resolve => resolve())
      this.bulkReadPromiseResolvers = []
    }
  }

  async markPostAsRead(postId: string, channelId: string): Promise<void> {
    // Prevent duplicate posts in the queue and already read posts
    if (this.alreadyReadPostIds.has(postId)) return
    if (this.bulkReadQueue.some(item => item.postId === postId)) return

    this.bulkReadQueue.push({ postId, channelId })
    this.startBulkReadTimer()

    return new Promise<void>(resolve => {
      this.bulkReadPromiseResolvers.push(resolve)
    })
  }
}

export class PostsState {
  state = reactive({
    fetching: false,
    error: null as any,
    pinnedPosts: new Map<string, IPosts[]>(),
    isNextPinnedPostsPageAvaialble: new Map<string, boolean>(),
    isPinnedPostsPageLoading: new Map<string, boolean>(),

    posts: [] as IPosts[],
    postPages: new Set(),
    nextPage: true,
  })

  private bulkReadManager = new BulkReadManager()

  async fetchPost(
    postId: string,
    addToStateIfNotFound = false
  ): Promise<IPosts | undefined> {
    try {
      const foundPost = PostState.state.posts.find(
        (post: any) => post._id === postId
      )
      if (foundPost) {
        return foundPost
      }
      const post = await PostsService.getPost(postId)
      post.relativeTime = convertToRelativeTime(post?.createdAt)
      if (addToStateIfNotFound) {
        this.state.posts.push(post)
      }
      return post
    } catch (error: any) {
      this.state.error = error?.response?.message || 'Something went wrong'
      console.error('error fetching post', error)
      throw error
    } finally {
      this.state.fetching = false
    }
  }
  async fetchPosts(route: any, query?: any): Promise<void> {
    try {
      let newPosts = []

      //Reset post if when fetching first page
      if (!query.previousId) {
        this.state.posts = []
        this.state.postPages = new Set()
        this.state.nextPage = true
      }

      //Page already fetched
      if (query.previousId && this.state.postPages.has(query.previousId)) {
        return
      }

      //nextPage Not available
      if (!this.state.nextPage) {
        return
      }

      this.state.fetching = true
      if (route.params?.channelSlug) {
        const channel = new Channel(
          GroupState.state.channelList.find(
            (channel: any) => channel.slug === route.params.channelSlug
          )
        )
        newPosts = await PostsService.getChannelPosts(channel.id, query)
        if (query.previousId) {
          this.state.posts.push(...newPosts)
        } else {
          this.state.posts = newPosts
        }
      } else if (route.params.userSlug) {
        try {
          newPosts = await PostsService.getPostsByUser({
            slug: route.params.userSlug,
            ...query,
          })
          if (query.previousId) {
            this.state.posts.push(...newPosts)
          } else {
            this.state.posts = newPosts
          }
        } catch (error: any) {
          // in case of user is not found in this case do not throw error ignore the error and do not update state.posts
          if (error?.response?.data?.message != 'User not found') {
            throw error
          }
        }
      } else if (route.params.groupSlug) {
        newPosts = await PostsService.getPublicPosts(query)
        this.state.posts.push(...newPosts)
      } else {
        newPosts = await PostsService.getPublicPosts(query)
        this.state.posts.push(...newPosts)
      }

      //To avoid repeative fetching of same page
      if (query.previousId) {
        this.state.postPages.add(query.previousId)
      }

      //Check if nextPage is available
      if (query.limit && newPosts.length < query.limit) {
        this.state.nextPage = false
      }

      this.state.posts = this.state.posts.map(post => {
        post.relativeTime = convertToRelativeTime(post.createdAt)
        return post
      })
    } catch (error: any) {
      if (error?.response?.status === 403) {
        this.state.posts = []
        this.state.postPages = new Set()
        this.state.nextPage = true
      }
      this.state.error = error?.response?.message || 'Something went wrong'
      console.error(error)
      throw error
    } finally {
      this.state.fetching = false
    }
  }

  async fetchPinnedPostsForChannel(
    channelSlug: string,
    paginationParams?: any
  ): Promise<void> {
    try {
      let existingPinnedPosts: IPosts[] | undefined = undefined

      if (
        this.state.isNextPinnedPostsPageAvaialble.get(channelSlug) === false
      ) {
        return
      }
      existingPinnedPosts = this.state.pinnedPosts.get(channelSlug)

      //Page already fetched
      if (existingPinnedPosts && paginationParams.previousPaginationKeyValue) {
        const prevPostIndex = existingPinnedPosts?.findIndex(
          post =>
            post.channelPinnedIndex ===
            paginationParams.previousPaginationKeyValue
        )
        // check if the previous post is the last post in the pinned posts list, if its not do nothing
        if (prevPostIndex != existingPinnedPosts.length - 1) {
          return
        }
      } else if (
        existingPinnedPosts &&
        !paginationParams.previousPaginationKeyValue
      ) {
        //since paginationKeyValue is not present, and first page is already fetched, do nothing
        return
      }

      // if the pinned posts page is already loading, do nothing
      if (this.state.isPinnedPostsPageLoading.get(channelSlug)) {
        return
      }

      this.state.isPinnedPostsPageLoading.set(channelSlug, true)
      const channel = new Channel(
        GroupState.state.channelList.find(
          (channel: any) => channel.slug === channelSlug
        )
      )
      const newPinnedPosts = await PostsService.getChannelPinnedPosts(
        channel.id,
        paginationParams
      )
      if (!existingPinnedPosts || existingPinnedPosts.length < 1) {
        this.state.pinnedPosts.set(channelSlug, [] as IPosts[])
        existingPinnedPosts = this.state.pinnedPosts.get(channelSlug)
      }
      // ensure that duplicate posts are not added to pinnedPost array
      newPinnedPosts.forEach((post: any) => {
        const postIndex = existingPinnedPosts?.findIndex(
          postInArray => postInArray._id === post._id
        )
        if (!postIndex || postIndex === -1) {
          post.relativeTime = convertToRelativeTime(post.createdAt)
          existingPinnedPosts?.push(post)
        }
      })
      this.state.isPinnedPostsPageLoading.set(channelSlug, false)
      //set next page available to false if the number of posts fetched is less than the limit
      if (
        paginationParams.limit &&
        newPinnedPosts.length < paginationParams.limit
      ) {
        this.state.isNextPinnedPostsPageAvaialble.set(channelSlug, false)
      } else {
        this.state.isNextPinnedPostsPageAvaialble.set(channelSlug, true)
      }
    } catch (error: any) {
      this.state.error = error?.response?.message || 'Something went wrong'
      console.error(error)
      throw error
    } finally {
      setTimeout(
        () => this.state.isPinnedPostsPageLoading.set(channelSlug, false),
        1000
      )
    }
  }

  async fetchPinnedPostsForHome(paginationParams?: any): Promise<void> {
    let groupSlug = GroupState.state.groupData.slug
    if (!groupSlug) {
      /*
      Wait for the group data to be loaded.
      When we switch to a new group, the group data is not loaded immediately.
      */
      await waitForGroupData()
      groupSlug = GroupState.state.groupData.slug
    }
    try {
      let existingPinnedPosts: IPosts[] | undefined = undefined

      if (this.state.isNextPinnedPostsPageAvaialble.get(groupSlug) === false) {
        return
      }
      existingPinnedPosts = this.state.pinnedPosts.get(groupSlug)

      //Page already fetched
      if (existingPinnedPosts && paginationParams.previousPaginationKeyValue) {
        const prevPostIndex = existingPinnedPosts?.findIndex(
          post =>
            post.homePinnedIndex === paginationParams.previousPaginationKeyValue
        )
        // check if the previous post is the last post in the pinned posts list, if its not do nothing
        if (prevPostIndex != existingPinnedPosts.length - 1) {
          return
        }
      } else if (
        existingPinnedPosts &&
        !paginationParams.previousPaginationKeyValue
      ) {
        //since paginationKeyValue is not present, and first page is already fetched, do nothing
        return
      }

      // if the pinned posts page is already loading, do nothing
      if (this.state.isPinnedPostsPageLoading.get(groupSlug)) {
        return
      }

      this.state.isPinnedPostsPageLoading.set(groupSlug, true)

      const newPinnedPosts = await PostsService.getHomePinnedPosts(
        paginationParams
      )
      if (!existingPinnedPosts || existingPinnedPosts.length < 1) {
        this.state.pinnedPosts.set(groupSlug, [] as IPosts[])

        existingPinnedPosts = this.state.pinnedPosts.get(groupSlug)
      }
      // ensure that duplicate posts are not added to pinnedPost array
      newPinnedPosts.forEach((post: any) => {
        const postIndex = existingPinnedPosts?.findIndex(
          postInArray => postInArray._id === post._id
        )
        if (!postIndex || postIndex === -1) {
          post.relativeTime = convertToRelativeTime(post.createdAt)
          existingPinnedPosts?.push(post)
        }
      })
      this.state.isPinnedPostsPageLoading.set(groupSlug, false)
      //set next page available to false if the number of posts fetched is less than the limit
      if (
        paginationParams.limit &&
        newPinnedPosts.length < paginationParams.limit
      ) {
        this.state.isNextPinnedPostsPageAvaialble.set(groupSlug, false)
      } else {
        this.state.isNextPinnedPostsPageAvaialble.set(groupSlug, true)
      }
    } catch (error: any) {
      this.state.error = error?.response?.message || 'Something went wrong'
      console.error(error)
      throw error
    } finally {
      setTimeout(
        () => this.state.isPinnedPostsPageLoading.set(groupSlug, false),
        1000
      )
    }
  }

  async addPostToPostList(post: IPosts): Promise<void> {
    //  make sure it's getting added to correct group
    if (post.groupId == GroupState.state.groupData._id) {
      const isPostAlreadyInList = this.state.posts.find(
        (p: IPosts) => p._id === post._id
      )
      if (!isPostAlreadyInList) {
        this.state.posts.unshift(post)
        GroupState.state.groupData.postsCount += 1
      }
    }
  }

  async updatePostStatus(post: IPosts, status: GoLiveStatus): Promise<void> {
    await PostsService.updatePostStatus(
      post._id as string,
      post.metadata?.licenseId as string,
      status
    )
  }
  async updatePostInPostList(
    postId: string,
    postMetadata: IPosts['metadata']
  ): Promise<void> {
    const postIndex = this.state.posts.findIndex(p => p._id === postId)
    if (postIndex !== -1) {
      this.state.posts[postIndex] = {
        ...this.state.posts[postIndex],
        metadata: postMetadata,
      }
    }
  }

  async createPost(
    postData: IPosts,
    updatePostList: boolean = true
  ): Promise<IPosts> {
    try {
      const post = new Posts(postData)

      await post.save()

      post.data.current_user_reactions = {
        LIKE: false,
      }
      post.data.relativeTime = convertToRelativeTime(post.data.createdAt)

      const channel = new Channel(
        GroupState.state.channelList.find(
          (channel: any) => channel._id === post.channelId
        )
      )
      if (updatePostList) {
        post.data.channelSlug = channel.slug
        this.state.posts.unshift(post.data)
        GroupState.state.groupData.postsCount += 1
      }

      // Mark post as read after creation
      this.markPostAsRead(post.data._id as string, post.channelId).catch(
        error => {
          console.error('Error marking post as read', error)
        }
      )

      trackCustomGAEvents(GAEventTypes.POST_CREATED, {
        createdBy: postData.createdBy,
        locationId: GroupState.state.groupData.locationId,
      })
      return post.data
    } catch (error: any) {
      console.error(error)
      throw error
    } finally {
      this.state.fetching = false
    }
  }

  async updatePost(postData: IPosts, updateState = false): Promise<any> {
    try {
      const updatedPost = await PostsService.updatePost(
        postData,
        postData.channelId,
        postData._id as string
      )

      // Update only specific fields in state if requested
      if (updateState) {
        const post = this.state.posts.find(
          (p: IPosts) => p._id === postData._id
        )
        if (post) {
          post.title = updatedPost.title
          post.content = updatedPost.content
          post.isCommentDisabled = updatedPost.isCommentDisabled ?? false
        }

        // Also update in pinnedPosts if present
        const groupSlug = GroupState.state.groupData?.slug
        if (groupSlug) {
          const pinnedPost = this.state.pinnedPosts
            .get(groupSlug)
            ?.find((p: IPosts) => p._id === postData._id)
          if (pinnedPost) {
            pinnedPost.title = updatedPost.title
            pinnedPost.content = updatedPost.content
            pinnedPost.isCommentDisabled =
              updatedPost.isCommentDisabled ?? false
          }
        }
      }

      return updatedPost
    } catch (error: any) {
      console.error(error)
      throw error
    }
  }

  async pinPostToChannelTimeline(
    postId: string,
    channelSlug: string
  ): Promise<void> {
    try {
      const channel = new Channel(
        GroupState.state.channelList.find(
          (channel: any) => channel.slug === channelSlug
        )
      )
      const pinnedPost = await PostsService.pinPostToChannel(postId, channel.id)
      const post = this.state.posts.find(post => post._id === postId)
      if (post) {
        post.channelPinnedIndex = pinnedPost.channelPinnedIndex

        // we need to deep clone post object so that the changes in the post object does not reflect in the pinned post object
        const clonedPost = JSON.parse(JSON.stringify(post))
        this.state.pinnedPosts.get(channelSlug)?.unshift(clonedPost!)
      }
      return pinnedPost.channelPinnedIndex
    } catch (error: any) {
      console.error(error)
    }
  }

  async pinPostToHomeTimeline(
    postId: string,
    channelId: string
  ): Promise<void> {
    try {
      const pinnedPost = await PostsService.pinPostToHome(postId, channelId)
      const post = this.state.posts.find(post => post._id === postId)
      if (post) {
        post.homePinnedIndex = pinnedPost.homePinnedIndex

        // we need to deep clone post object so that the changes in the post object does not reflect in the pinned post object
        const clonedPost = JSON.parse(JSON.stringify(post))
        this.state.pinnedPosts
          .get(GroupState.state.groupData.slug)
          ?.unshift(clonedPost)
      }

      return pinnedPost.homePinnedIndex
    } catch (error: any) {
      console.error(error)
    }
  }

  async unpinPostFromChannelTimeline(
    postId: string,
    channelSlug: string
  ): Promise<void> {
    try {
      const channel = new Channel(
        GroupState.state.channelList.find(
          (channel: any) => channel.slug === channelSlug
        )
      )
      const pinnedPost = await PostsService.unpinPostFromChannel(
        postId,
        channel.id
      )
      const post = this.state.posts.find(post => post._id === postId)
      if (post) {
        post.channelPinnedIndex = pinnedPost.channelPinnedIndex
      }
      const pinnedPostIndex = this.state.pinnedPosts
        ?.get(channelSlug)
        ?.findIndex(post => post._id === postId)
      if (pinnedPostIndex !== undefined) {
        this.state.pinnedPosts?.get(channelSlug)?.splice(pinnedPostIndex, 1)
      }
      return pinnedPost.channelPinnedIndex
    } catch (error: any) {
      console.error(error)
    }
  }

  async unpinPostFromHomeTimeline(
    postId: string,
    channelId: string
  ): Promise<void> {
    try {
      const pinnedPost = await PostsService.unpinPostFromHome(postId, channelId)
      const post = this.state.posts.find(post => post._id === postId)
      if (post) {
        post.homePinnedIndex = pinnedPost.homePinnedIndex
      }
      const pinnedPostIndex = this.state.pinnedPosts
        ?.get(GroupState.state.groupData.slug)
        ?.findIndex(post => post._id === postId)
      if (pinnedPostIndex !== undefined) {
        this.state.pinnedPosts
          ?.get(GroupState.state.groupData.slug)
          ?.splice(pinnedPostIndex, 1)
      }
      return pinnedPost.homePinnedIndex
    } catch (error: any) {
      console.error(error)
    }
  }

  async movePostToChannel(
    postId: string | undefined,
    channelId: string,
    route: any
  ): Promise<any> {
    try {
      if (!postId) {
        throw new Error('Post ID is required')
      }
      await PostsService.updatePostChannel(postId, channelId)
      const post = await PostsService.getPost(postId)
      post.relativeTime = convertToRelativeTime(post.createdAt)
      // if channel view is open then remove post from the timeline
      if (route?.params?.channelSlug) {
        console.log('removing post from channel')
        this.state.posts = this.state.posts.filter(p => p._id !== postId)
      }
      handlePostEdited(post)
      return post
    } catch (error: any) {
      console.error(error)
    }
  }

  async deletePost(postId: string): Promise<void> {
    const postIndex = this.state.posts.findIndex(post => postId === post._id)
    if (postIndex !== -1) {
      this.state.posts.splice(postIndex, 1)
    }
    GroupState.state.groupData.postsCount -= 1
    PostsService.deletePost(postId)
  }

  async toggleCommentsDisabled(
    postId: string,
    channelId: string,
    currentlyDisabled: boolean
  ): Promise<IPosts> {
    try {
      // Call the service
      const updatedPost = await PostsService.toggleCommentsDisabled(
        postId,
        channelId,
        currentlyDisabled
      )
      return updatedPost
    } catch (error) {
      throw error
    }
  }

  async markPostAsRead(postId: string, channelId: string): Promise<void> {
    const appStore = useGlobalStore()

    // Early return if user is not logged in
    if (!appStore.isAuthenticated) {
      return
    }

    // Early return if user does not have active group membership
    if (!GroupState.isActiveMember()) {
      return
    }

    return this.bulkReadManager.markPostAsRead(postId, channelId)
  }
}

export const PostState = new PostsState()
